import { FormControlLabel, Radio, RadioGroup, Table } from "@material-ui/core";
import Button from "@material-ui/core/Button";
import CircularProgress from "@material-ui/core/CircularProgress";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import { makeStyles } from "@material-ui/core/styles";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableRow from "@material-ui/core/TableRow";
import TextField from "@material-ui/core/TextField";
import ConfirmButton, {
  ConfirmButtonTransitionState
} from "@saleor/components/ConfirmButton";
import Form from "@saleor/components/Form";
import FormSpacer from "@saleor/components/FormSpacer";
import ResponsiveTable from "@saleor/components/ResponsiveTable";
import SingleAutocompleteSelectField from "@saleor/components/SingleAutocompleteSelectField";
import useStateFromProps from "@saleor/hooks/useStateFromProps";
import { buttonMessages } from "@saleor/intl";
import { maybe } from "@saleor/misc";
import { SearchProducts_search_edges_node } from "@saleor/searches/types/SearchProducts";
import useScrollableDialogStyle from "@saleor/styles/useScrollableDialogStyle";
import { FetchMoreProps } from "@saleor/types";
import { CoverageTypeEnum } from "@saleor/types/globalTypes";
import createSingleAutocompleteSelectHandler from "@saleor/utils/handlers/singleAutocompleteSelectChangeHandler";
import React from "react";
import InfiniteScroll from "react-infinite-scroller";
import { FormattedMessage, useIntl } from "react-intl";

import Checkbox from "../Checkbox";

const useStyles = makeStyles(
  {
    checkboxCell: {
      paddingLeft: 0,
      width: 88
    },
    colName: {
      paddingLeft: 0
    },
    searchBy: {
      marginBottom: 10
    }
  },
  { name: "AssignProductDialog" }
);

export interface AssignProductDialogProps extends FetchMoreProps {
  open: boolean;
  loading: boolean;
  onClose: () => void;
  onSubmit: (data: SearchProducts_search_edges_node[]) => void;
  storesBrands: any;
  fetchStoreBrands?: (query: string) => void;
  fetchMoreStoreBrands?: FetchMoreProps;
  confirmButtonState: ConfirmButtonTransitionState;
  products: SearchProducts_search_edges_node[];
  onSelectStoreBrand: (brand: string, mall?: string) => void;
  handleSearchBy?: (event: any) => void;
  handleProductSearch?: (event: any) => void;
  productSearch?: string;
  setProductSearch?: (value: string) => void;
  coverageType: CoverageTypeEnum | null | undefined;
}

function handleProductAssign(
  product: SearchProducts_search_edges_node,
  isSelected: boolean,
  selectedProducts: SearchProducts_search_edges_node[],
  setSelectedProducts: (data: SearchProducts_search_edges_node[]) => void
) {
  if (isSelected) {
    setSelectedProducts(
      selectedProducts.filter(
        selectedProduct => selectedProduct.id !== product.id
      )
    );
  } else {
    setSelectedProducts([...selectedProducts, product]);
  }
}

function handleSelectAll(
  products: SearchProducts_search_edges_node[],
  setSelectedProducts: (data: SearchProducts_search_edges_node[]) => void
) {
  const allProducts: SearchProducts_search_edges_node[] = [];
  products.forEach(product => {
    if (
      !allProducts.some(selectedProducts => selectedProducts.id === product.id)
    ) {
      allProducts.push(product);
    }
  });
  setSelectedProducts(allProducts);
}

const AssignProductDialog: React.FC<AssignProductDialogProps> = props => {
  const {
    open,
    loading,
    onClose,
    onSubmit,
    hasMore,
    onFetchMore,
    storesBrands,
    fetchStoreBrands,
    fetchMoreStoreBrands,
    confirmButtonState,
    products,
    onSelectStoreBrand,
    handleSearchBy,
    productSearch,
    handleProductSearch,
    coverageType
  } = props;
  const classes = useStyles(props);
  const scrollableDialogClasses = useScrollableDialogStyle({});

  const intl = useIntl();
  const [selectedProducts, setSelectedProducts] = React.useState<
    SearchProducts_search_edges_node[]
  >([]);
  const container = React.useRef<HTMLDivElement>();
  const [isSelectAll, setSelectAll] = React.useState(false);
  const handleSubmit = () => onSubmit(selectedProducts);
  const [sellerDisplayName, setSellerDisplayName] = useStateFromProps("");
  const initialForm = {
    id: null,
    name: "",
    sellerBrand: ""
  };

  const storesBrandsChoices = () => {
    if (coverageType === CoverageTypeEnum.NATIONWIDE) {
      return maybe(
        () =>
          storesBrands.map(
            (storeBrand: {
              brand: { name: string; id: any };
              mall: { name: string; id: any };
              id: any;
            }) => ({
              label: storeBrand?.brand?.name + " - " + storeBrand?.mall?.name,
              mallId: storeBrand?.mall?.id,
              brandId: storeBrand?.brand?.id,
              value: storeBrand?.id
            })
          ),
        []
      );
    } else {
      return maybe(
        () =>
          storesBrands.map(
            (storeBrand: { name: string; legalName: string; id: any }) => ({
              label: storeBrand?.name + " - " + storeBrand.legalName,
              value: storeBrand?.id
            })
          ),
        []
      );
    }
  };

  React.useEffect(() => {
    if (!open) {
      setSelectedProducts([]);
    }
  }, [open]);

  React.useEffect(() => {
    if (isSelectAll) {
      handleSelectAll(products, setSelectedProducts);
    }
  }, [products]);

  return (
    <Form initial={initialForm} onSubmit={handleSubmit} confirmLeave>
      {({ data: formData, change, submit }) => {
        const handleSelectBrand = createSingleAutocompleteSelectHandler(
          change,
          setSellerDisplayName,
          storesBrandsChoices()
        );

        let btnDisabled = false;
        if (!formData.sellerBrand) {
          btnDisabled = true;
        } else if (formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else if (!formData.sellerBrand && products && products.length) {
          btnDisabled = false;
        } else {
          btnDisabled = true;
        }
        return (
          <Dialog
            onClose={onClose}
            open={open}
            classes={{ paper: scrollableDialogClasses.dialog }}
            fullWidth
            maxWidth="sm"
          >
            <DialogTitle>
              <FormattedMessage
                defaultMessage="Assign Product"
                description="dialog header"
              />
            </DialogTitle>
            <DialogContent
              className={scrollableDialogClasses.content}
              ref={container}
            >
              <SingleAutocompleteSelectField
                label={intl.formatMessage({
                  defaultMessage: "Store Name:"
                })}
                name="sellerBrand"
                displayValue={sellerDisplayName}
                choices={storesBrandsChoices()}
                disabled={false}
                value={formData.sellerBrand}
                onChange={e => {
                  let mallId = "";
                  let brandId = e.target.value;
                  if (coverageType === CoverageTypeEnum.NATIONWIDE) {
                    const selectedItem = storesBrandsChoices().find(
                      (choice: { value: any }) =>
                        choice.value === e.target.value
                    );
                    if (selectedItem) {
                      mallId = selectedItem.mallId;
                      brandId = selectedItem.brandId;
                    }
                  }

                  handleSelectBrand(e);
                  onSelectStoreBrand(brandId, mallId);
                }}
                fetchChoices={fetchStoreBrands}
                {...fetchMoreStoreBrands}
              />
              <FormSpacer />
              <RadioGroup
                row
                className={classes.searchBy}
                aria-label="searchBy"
                name="searchBy"
                defaultValue="sku"
                onChange={event => handleSearchBy(event.target.value)}
              >
                <FormControlLabel
                  disabled={!formData.sellerBrand ? true : false}
                  value="sku"
                  control={<Radio />}
                  label="Search by SKU"
                />
                <FormControlLabel
                  disabled={!formData.sellerBrand ? true : false}
                  value="name"
                  control={<Radio />}
                  label="Search by Product Name"
                />
              </RadioGroup>
              <TextField
                name="query"
                value={productSearch}
                disabled={!formData.sellerBrand ? true : false}
                onChange={handleProductSearch}
                label={intl.formatMessage({
                  defaultMessage: "Search Products"
                })}
                fullWidth
                InputProps={{
                  autoComplete: "off",
                  endAdornment: loading && <CircularProgress size={16} />
                }}
              />

              {formData.sellerBrand ? (
                <>
                  <FormSpacer />
                  <div
                    className={scrollableDialogClasses.scrollArea}
                    style={{ height: "33vh" }}
                  >
                    <InfiniteScroll
                      pageStart={0}
                      loadMore={onFetchMore}
                      hasMore={hasMore}
                      useWindow={false}
                      loader={
                        <div
                          className={
                            scrollableDialogClasses.loadMoreLoaderContainer
                          }
                          key={0}
                        >
                          <CircularProgress size={16} />
                        </div>
                      }
                      threshold={10}
                    >
                      <ResponsiveTable key="table">
                        <TableBody>
                          {products && products.length ? (
                            <TableRow key="all">
                              <TableCell className={classes.colName}>
                                Select All
                              </TableCell>
                              <TableCell
                                padding="checkbox"
                                className={classes.checkboxCell}
                              >
                                <Checkbox
                                  checked={isSelectAll}
                                  onChange={e => {
                                    setSelectAll(!isSelectAll);
                                    if (e.target.checked) {
                                      handleSelectAll(
                                        products,
                                        setSelectedProducts
                                      );
                                    } else {
                                      setSelectedProducts([]);
                                    }
                                  }}
                                />
                              </TableCell>
                            </TableRow>
                          ) : null}

                          {products &&
                            products.map((product, index) => {
                              const isSelected = selectedProducts.some(
                                selectedProduct =>
                                  selectedProduct.id === product.id
                              );

                              return (
                                <TableRow key={product.id}>
                                  <TableCell
                                    className={classes.colName}
                                    id={`productNameRow${index}`}
                                  >
                                    {product.name}
                                  </TableCell>
                                  <TableCell
                                    padding="checkbox"
                                    className={classes.checkboxCell}
                                  >
                                    <Checkbox
                                      checked={isSelected}
                                      onChange={e => {
                                        if (!e.target.checked) {
                                          setSelectAll(false);
                                        }
                                        handleProductAssign(
                                          product,
                                          isSelected,
                                          selectedProducts,
                                          setSelectedProducts
                                        );
                                      }}
                                    />
                                  </TableCell>
                                </TableRow>
                              );
                            })}
                        </TableBody>
                      </ResponsiveTable>
                    </InfiniteScroll>
                    {products && products.length ? (
                      ""
                    ) : (
                      <Table>
                        <TableBody>
                          <TableRow>
                            <TableCell>No Products Found</TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    )}
                  </div>
                </>
              ) : (
                ""
              )}
            </DialogContent>
            <DialogActions>
              <Button onClick={onClose}>
                <FormattedMessage {...buttonMessages.back} />
              </Button>
              <ConfirmButton
                transitionState={confirmButtonState}
                color="primary"
                variant="contained"
                type="submit"
                onClick={submit}
                disabled={btnDisabled}
              >
                <FormattedMessage
                  defaultMessage="Assign products"
                  description="button"
                />
              </ConfirmButton>
            </DialogActions>
          </Dialog>
        );
      }}
    </Form>
  );
};
AssignProductDialog.displayName = "AssignProductDialog";
export default AssignProductDialog;
